import httpClient from './httpClient';

class AuthService {
  // Login with email/username and password
  async loginWithPassword(emailOrUsername, password) {
    try {
      const response = await httpClient.post('/api/auth/login', {
        email_or_username: emailOrUsername,
        password: password
      });
      return response.data;
    } catch (error) {
      console.error('Error in password login:', error);
      throw error;
    }
  }

  // Register new user
  async register(email, username, password, confirmPassword) {
    try {
      const response = await httpClient.post('/api/auth/register', {
        email: email,
        username: username,
        password: password,
        confirm_password: confirmPassword
      });
      return response.data;
    } catch (error) {
      console.error('Error in registration:', error);
      throw error;
    }
  }

  // Generate WeChat QR code for login
  async generateQRCode() {
    try {
      const response = await httpClient.post('/api/auth/wechat/qr-code');
      return response.data;
    } catch (error) {
      console.error('Error generating QR code:', error);
      throw error;
    }
  }

  // Send OTP to phone number
  async sendPhoneOtp(phoneNumber) {
    try {
      const response = await httpClient.post('/api/auth/phone/send-otp', {
        phone_number: phoneNumber
      });
      return response.data;
    } catch (error) {
      console.error('Error sending phone OTP:', error);
      throw error;
    }
  }

  // Login with phone number and OTP
  async loginWithPhoneOtp(phoneNumber, otp) {
    try {
      const response = await httpClient.post('/api/auth/phone/login', {
        phone_number: phoneNumber,
        otp: otp
      });
      return response.data;
    } catch (error) {
      console.error('Error logging in with phone OTP:', error);
      throw error;
    }
  }

  // Check WeChat login status
  async checkLoginStatus(qrCodeId) {
    try {
      const response = await httpClient.get(`/api/auth/wechat/check-status/${qrCodeId}`);
      return response.data;
    } catch (error) {
      console.error('Error checking login status:', error);
      throw error;
    }
  }

  // Get user profile
  async getUserProfile() {
    try {
      const response = await httpClient.get('/api/auth/profile');
      return response.data;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  }

  // Update user profile
  async updateProfile(profileData) {
    try {
      const response = await httpClient.put('/api/auth/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Logout user
  async logout() {
    try {
      await httpClient.post('/api/auth/logout');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if logout fails, clear local storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    } finally {
      // Clear all filter persistence from sessionStorage
      this.clearAllFilterPersistence();

      // Dispatch custom events to notify components
      window.dispatchEvent(new CustomEvent('authStateChanged'));
      window.dispatchEvent(new CustomEvent('userLogout'));
    }
  }

  // Clear all filter persistence data from sessionStorage
  clearAllFilterPersistence() {
    try {
      // Get all keys from sessionStorage
      const keys = Object.keys(sessionStorage);

      // Remove all keys that start with 'filters_'
      keys.forEach(key => {
        if (key.startsWith('filters_')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing filter persistence:', error);
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('access_token');
    return !!token;
  }

  // Get current user from localStorage
  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  // Validate user permissions with server (SECURITY FIX)
  async validateUserPermissions() {
    try {
      if (!this.isAuthenticated()) {
        return { isValid: false, permissions: [] };
      }
      
      const response = await httpClient.get('/api/auth/me/permissions');
      return {
        isValid: true,
        permissions: response.data.permissions || [],
        role: response.data.role,
        user: response.data.user
      };
    } catch (error) {
      console.error('Error validating permissions:', error);
      // On error, revoke access for security
      this.logout();
      return { isValid: false, permissions: [] };
    }
  }

  // Check if user has specific permission (client-side check only after server validation)
  hasPermission(permission, userPermissions = []) {
    if (!userPermissions.length) {
      const user = this.getCurrentUser();
      // Fallback to role-based check but this should NOT be the primary security
      return user?.role === 'admin';
    }
    return userPermissions.includes(permission);
  }

  // Check if current user is admin
  isAdmin() {
    const user = this.getCurrentUser();
    return user && user.role === 'admin';
  }

  // Get access token from localStorage
  getToken() {
    return localStorage.getItem('access_token');
  }

  // Get users list (admin only)
  async getUsers(page = 1, pageSize = 50, role = null, status = null) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      if (role) params.append('role', role);
      if (status) params.append('status', status);
      
      const response = await httpClient.get(`/api/auth/users?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error getting users:', error);
      throw error;
    }
  }

  // Update user status (admin only)
  async updateUserStatus(userId, isActive) {
    try {
      const response = await httpClient.put(`/api/auth/users/${userId}/status`, {
        is_active: isActive
      });
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  // Update user role (admin only)
  async updateUserRole(userId, role) {
    try {
      const response = await httpClient.put(`/api/auth/users/${userId}/role`, {
        role: role
      });
      return response.data;
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }

  // Forgot password - send reset email
  async forgotPassword(email) {
    try {
      const response = await httpClient.post('/api/auth/forgot-password', {
        email: email
      });
      return response.data;
    } catch (error) {
      console.error('Error in forgot password:', error);
      throw error;
    }
  }

  // Verify reset token
  async verifyResetToken(token) {
    try {
      const response = await httpClient.post('/api/auth/verify-reset-token', {
        token: token
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying reset token:', error);
      throw error;
    }
  }

  // Reset password with token
  async resetPassword(token, password, confirmPassword) {
    try {
      const response = await httpClient.post('/api/auth/reset-password', {
        token: token,
        password: password,
        confirm_password: confirmPassword
      });
      return response.data;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }

  // Store user data after successful login
  storeUserData(userData, token) {
    localStorage.setItem('access_token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    // Dispatch custom event to notify App component of auth state change
    window.dispatchEvent(new CustomEvent('authStateChanged'));
  }

  // ============== Casdoor SSO Methods ==============

  /**
   * Exchange Casdoor authorization code for access token
   * @param {string} code - Authorization code from OAuth callback
   * @param {string} state - Optional state parameter
   * @returns {Promise<Object>} Response with user and access_token
   */
  async casdoorSignin(code, state = '') {
    try {
      const response = await httpClient.post('/api/auth/casdoor/signin', {
        code,
        state
      });
      return response.data;
    } catch (error) {
      console.error('Error in Casdoor signin:', error);
      throw error;
    }
  }

  /**
   * Get Casdoor configuration from backend
   * @returns {Promise<Object>} Casdoor config (enabled, endpoint, clientId, etc.)
   */
  async getCasdoorConfig() {
    try {
      const response = await httpClient.get('/api/auth/casdoor/config');
      return response.data;
    } catch (error) {
      console.error('Error getting Casdoor config:', error);
      throw error;
    }
  }

  /**
   * Logout with SSO support
   * Clears local storage and optionally redirects to Casdoor logout
   * @param {boolean} ssoLogout - Whether to perform SSO logout
   */
  async logoutWithSSO(ssoLogout = false) {
    try {
      // First, logout from local backend
      await httpClient.post('/api/auth/logout');
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');

      // Clear all filter persistence
      this.clearAllFilterPersistence();

      // Dispatch events
      window.dispatchEvent(new CustomEvent('authStateChanged'));
      window.dispatchEvent(new CustomEvent('userLogout'));

      // If SSO logout requested, redirect to Casdoor logout
      if (ssoLogout) {
        const config = await this.getCasdoorConfig().catch(() => null);
        if (config?.enabled && config?.endpoint) {
          const logoutUrl = `${config.endpoint}/api/logout?redirectUri=${encodeURIComponent(window.location.origin + '/login')}`;
          window.location.href = logoutUrl;
          return; // Don't navigate, we're redirecting
        }
      }
    }
  }
}

const authService = new AuthService();
export default authService; 